/**
 * Phase 20 Plan 20-02 — EditRecipientDialog
 *
 * Pre-fills from RecipientDetailDto. Type + UserId are immutable (D-184).
 * Only sends displayName / email / isActive on submit.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateRecipientMutation } from '@/hooks/useNotificationsQueries';
import { toast } from '@/lib/toast';
import type { RecipientDetailDto } from '@/types/notifications';

const editRecipientSchema = z.object({
  displayName: z.string().min(1, 'Display name is required').max(200),
  email: z
    .string()
    .email('Invalid email address')
    .optional()
    .or(z.literal('')),
  isActive: z.boolean(),
});

type EditRecipientFormValues = z.infer<typeof editRecipientSchema>;

interface EditRecipientDialogProps {
  readonly open: boolean;
  readonly recipient: RecipientDetailDto;
  readonly onClose: () => void;
}

export function EditRecipientDialog({ open, recipient, onClose }: EditRecipientDialogProps): JSX.Element {
  const updateMutation = useUpdateRecipientMutation();

  const form = useForm<EditRecipientFormValues>({
    resolver: zodResolver(editRecipientSchema),
    defaultValues: {
      displayName: recipient.displayName,
      email: recipient.email ?? '',
      isActive: recipient.isActive,
    },
    mode: 'onSubmit',
  });

  // Re-seed form if recipient prop changes
  useEffect(() => {
    form.reset({
      displayName: recipient.displayName,
      email: recipient.email ?? '',
      isActive: recipient.isActive,
    });
  }, [recipient, form]);

  const onSubmit = async (values: EditRecipientFormValues): Promise<void> => {
    try {
      const emailValue = values.email !== '' ? values.email : undefined;
      const body =
        emailValue !== undefined
          ? { displayName: values.displayName, email: emailValue, isActive: values.isActive }
          : { displayName: values.displayName, isActive: values.isActive };
      await updateMutation.mutateAsync({
        id: recipient.id,
        body,
      });
      toast.success('Recipient updated');
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update recipient';
      form.setError('root', { message: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-recipient-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Recipient</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => { void form.handleSubmit(onSubmit)(e); }}
          className="space-y-4"
        >
          {/* Display Name */}
          <div className="space-y-1">
            <Label htmlFor="edit-displayName">Display Name *</Label>
            <Input
              id="edit-displayName"
              data-testid="field-displayName"
              {...form.register('displayName')}
            />
            {form.formState.errors.displayName !== undefined && (
              <p className="text-xs text-destructive" data-testid="error-edit-displayName">
                {form.formState.errors.displayName.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <Label htmlFor="edit-email">Email (optional)</Label>
            <Input
              id="edit-email"
              data-testid="field-email"
              type="email"
              {...form.register('email')}
            />
            {form.formState.errors.email !== undefined && (
              <p className="text-xs text-destructive" data-testid="error-edit-email">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          {/* Type (immutable) */}
          <div className="space-y-1">
            <Label>Type</Label>
            <p
              data-testid="field-type-readonly"
              className="rounded border bg-muted px-2 py-1 text-sm text-muted-foreground"
            >
              {recipient.type}
              <span className="ml-2 text-xs">(Immutable per D-184)</span>
            </p>
          </div>

          {/* User ID (immutable) */}
          {recipient.userId !== null && (
            <div className="space-y-1">
              <Label>User ID</Label>
              <p
                data-testid="field-userId-readonly"
                className="rounded border bg-muted px-2 py-1 text-sm text-muted-foreground"
              >
                {recipient.userId}
                <span className="ml-2 text-xs">(Immutable per D-184)</span>
              </p>
            </div>
          )}

          {/* Is Active */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="edit-isActive"
              data-testid="field-isActive"
              checked={form.watch('isActive')}
              onCheckedChange={(v) => { form.setValue('isActive', v === true); }}
            />
            <Label htmlFor="edit-isActive">Active</Label>
          </div>

          {/* Root-level error */}
          {form.formState.errors.root !== undefined && (
            <p className="text-xs text-destructive" data-testid="edit-recipient-error">
              {form.formState.errors.root.message}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={updateMutation.isPending}
              data-testid="edit-recipient-submit-btn"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
